import React, { useLayoutEffect, useRef } from 'react';
import { Sparkle, UploadSimple } from '@phosphor-icons/react';
import tw, { css, styled } from 'twin.macro';
import { AdCreativeType, AdPlacement } from 'types/adTypes';
import useAdEditorContext from 'hooks/context/ad-editor-context';
import { fabric } from 'fabric';
import { AdEditorSideBarTabs } from 'views/ad-builder/AdBuilder';
import { initAligningGuidelines } from 'utils/fabricjs/aligning_guidelines';
import { getCanvasAspect } from 'utils/adEditorCanvasHelpers';
import Divider from 'components/divider/Divider';
import useVideoGenContext from 'hooks/context/video-gen-context';
import { VIDEO_LOCKED_MESSAGE } from 'utils/videoGenerationLocks';
import { PostHogFeature, useFeatureFlagEnabled } from 'posthog-js/react';

export const AdEditorStencilCanvas: React.FC = () => {
  const {
    currentVariant,
    setActiveTab,
    setUploadMode,
    setCanvas,
    canvasWidth,
    canvasHeight,
    hasAdTemplatesV2,
    templatePreviews,
  } = useAdEditorContext();

  const svgCanvasRef = useRef<HTMLCanvasElement>(null);

  useLayoutEffect(() => {
    const canvaz = new fabric.Canvas(svgCanvasRef.current, {
      selection: true,
      preserveObjectStacking: true,
    });
    setCanvas(canvaz);
    initAligningGuidelines(canvaz);
  }, []);

  const { videoGen, bringToLife, videoLocks } = useVideoGenContext();

  const hasT1 = useFeatureFlagEnabled('video-track-1-scenario');
  // TODO: drop `ai-video` fallback once T1 rollout is complete and the legacy flag is retired.
  const hasLegacyAiVideo = useFeatureFlagEnabled('ai-video');
  const showT1Entry = hasT1 || hasLegacyAiVideo;

  const isImageVariant =
    currentVariant?.path &&
    currentVariant?.creative_type !== AdCreativeType.VIDEO;

  // SYM-5797. A v2 template is drawn on the SERVER -- the canvas underneath is still showing the
  // legacy composite, which is not what will be published. Cover it with the real thing.
  // Read by code, so what is shown always belongs to the template the variant currently carries.
  const templateCode = currentVariant?.template ?? '';
  const templatePreview = templateCode
    ? templatePreviews[templateCode]
    : undefined;
  const showsTemplate = Boolean(templateCode);

  return (
    <AdImageCanvasWrapper
      placement={currentVariant?.placement}
      portraitFeed={hasAdTemplatesV2}
    >
      <AdImageCanvas
        ref={svgCanvasRef}
        height={canvasHeight}
        width={canvasWidth}
        tabIndex={1212}
      />
      {showsTemplate && (
        <TemplatePreviewLayer id="template-preview">
          {templatePreview?.url ? (
            <TemplatePreviewImage
              src={templatePreview.url}
              alt={`${currentVariant?.template} template preview`}
            />
          ) : (
            <div tw="m-auto text-center text-sm text-gray-600 px-4">
              {templatePreview?.error ??
                (templatePreview?.waiting
                  ? 'The server is busy right now. Trying again shortly.'
                  : 'Rendering this template on the server…')}
            </div>
          )}
        </TemplatePreviewLayer>
      )}
      {!currentVariant?.path && (
        <div
          id="placeholder"
          tw="h-full w-full flex justify-center absolute z-10 top-0 left-0 bg-gray-200"
        >
          <div tw="my-auto h-fit items-center">
            <div
              onClick={() => {
                setActiveTab(AdEditorSideBarTabs.VISUAL_OPTIONS);
                setUploadMode(true);
              }}
              tw="flex flex-col items-center justify-center text-gray-500 font-semibold cursor-pointer"
            >
              <UploadSimple weight="bold" size={32} tw="my-2" />
              Click here to upload an image/video
            </div>
            {/* SYM-6166. Greyed out while a render holds the lock, never replaced by a message:
                what the customer is looking at stays what it was. The reason is on hover. */}
            {showT1Entry && (
              <>
                <Divider dark />
                <div
                  onClick={videoLocks.creatomate ? undefined : videoGen}
                  title={
                    videoLocks.creatomate ? VIDEO_LOCKED_MESSAGE : undefined
                  }
                  css={[
                    tw`flex flex-col items-center mt-2 justify-center font-semibold`,
                    videoLocks.creatomate
                      ? tw`text-gray-400 cursor-not-allowed`
                      : tw`text-gray-500 cursor-pointer`,
                  ]}
                >
                  <Sparkle weight="fill" size={32} tw="my-1" />
                  Generate a video using AI (beta)
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <PostHogFeature flag="video-track-2-bring-to-life" match={true}>
        {/* SYM-6166. One button, greyed out while the clip is being made, with the reason on
            hover. It keeps the id: the wrapper forces everything inside it to the full size of the
            ad and excuses this element by name, so anything drawn under a different name balloons
            over the customer's own picture. That is what the note it replaces did. */}
        {isImageVariant && (
          <BringToLifeButton
            id="btl-btn"
            onClick={bringToLife}
            disabled={videoLocks.fal}
            $locked={videoLocks.fal}
            title={videoLocks.fal ? VIDEO_LOCKED_MESSAGE : undefined}
          >
            <Sparkle weight="fill" size={12} />
            Bring to life
          </BringToLifeButton>
        )}
      </PostHogFeature>
    </AdImageCanvasWrapper>
  );
};

/**
 * SYM-6006. The visible frame around the ad.
 *
 * ⚠️ THIS IS WHERE THE SQUARE LIVED, not on the canvas element. Hiding or resizing the canvas alone
 * would not have changed the shape the customer sees — the wrapper's aspect is what frames it, and
 * the server-rendered preview below is `absolute inset-0` inside this box.
 *
 * The ratio now comes from getCanvasAspect() rather than being written out here, so the frame and
 * the canvas dimensions cannot drift apart. They were separate before, which is exactly how the
 * frame could say square while the render was portrait.
 */
export const AdImageCanvasWrapper = styled.div<{
  placement?: AdPlacement;
  portraitFeed?: boolean;
}>`
  ${tw`w-full overflow-hidden relative`}

  ${css`
    *:not(#placeholder *):not(#btl-btn):not(#btl-btn *) {
      width: 100% !important;
      height: 100% !important;
    }
  `}

  ${({ placement, portraitFeed }) => {
    const aspect = getCanvasAspect(placement, portraitFeed);
    return aspect ? css({ aspectRatio: aspect }) : undefined;
  }}
`;

const AdImageCanvas = styled.canvas(
  tw`w-full relative z-10 top-0 left-0 object-fill`
);

/**
 * Sits ABOVE the fabric canvas (z-10) and below the Bring-to-Life button (z-20). The canvas stays
 * mounted underneath -- fabric needs it, and the customer may switch back to a legacy overlay -- it
 * is simply not what gets published while a v2 template is selected.
 */
const TemplatePreviewLayer = styled.div(
  tw`absolute inset-0 z-[15] flex bg-gray-100`
);

const TemplatePreviewImage = styled.img(tw`w-full h-full object-contain`);

/**
 * SYM-6166. The two looks are chosen in the component and passed in, not left to a `:disabled`
 * rule. Measured in the browser on 2026-09-07: with the button genuinely disabled — matching
 * `:disabled`, not matching `:enabled`, and a `:disabled` rule present carrying the grey — the
 * computed background still came back indigo. Rather than keep guessing at that cascade, the state
 * decides the colour where the state is known.
 */
const BringToLifeButton = styled.button<{ $locked?: boolean }>`
  ${tw`absolute bottom-2 right-2 z-20 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors`}
  ${({ $locked }) =>
    $locked
      ? tw`text-gray-600 bg-gray-300 cursor-not-allowed`
      : tw`text-white bg-indigo-500 hover:bg-indigo-600 shadow cursor-pointer`}
`;

export default AdEditorStencilCanvas;
